import api from './api';
import { ApiResponse } from '../types/api';
import type {
  LessonWrongAnswers,
  SubmitQuizWithAnswersRequest,
  WrongAnswerItem,
  WrongAnswerLesson,
} from '../types/wrongAnswer';

function getErrorMessage(body: ApiResponse<unknown> | undefined, fallback: string): string {
  if (!body) return fallback;
  if (typeof body.message === 'string' && body.message) return body.message;
  if (typeof body.data === 'string' && body.data) return body.data;
  return fallback;
}

function readNumber(...values: unknown[]): number {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

function readText(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function firstArray(...candidates: unknown[]): unknown[] | null {
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }
  return null;
}

function normalizeLesson(raw: Record<string, unknown>): WrongAnswerLesson | null {
  const lessonId = readNumber(raw.lessonId, raw.LessonId, raw.id, raw.Id);
  if (!lessonId) return null;
  return {
    lessonId,
    lessonName: readText(raw.lessonName, raw.LessonName, raw.quizName, raw.QuizName, raw.name, raw.Name) || 'اختبار',
    courseName: readText(raw.courseName, raw.CourseName),
    unitName: readText(raw.unitName, raw.UnitName),
    wrongCount:
      readNumber(
        raw.totalWrongQuestions,
        raw.TotalWrongQuestions,
        raw.wrongCount,
        raw.WrongCount,
        raw.wrongAnswersCount,
        raw.WrongAnswersCount,
        raw.count,
        raw.Count
      ) || firstArray(raw.wrongQuestions, raw.WrongQuestions)?.length || 0,
  };
}

function normalizeQuestion(raw: Record<string, unknown>): WrongAnswerItem | null {
  const questionText = readText(
    raw.questionText,
    raw.QuestionText,
    raw.text,
    raw.Text,
    raw.question,
    raw.Question
  );
  if (!questionText) return null;
  return {
    questionText,
    studentAnswer: readText(
      raw.studentAnswer,
      raw.StudentAnswer,
      raw.selectedAnswer,
      raw.SelectedAnswer,
      raw.studentAnswerText,
      raw.StudentAnswerText
    ) || '—',
    correctAnswer: readText(
      raw.correctAnswer,
      raw.CorrectAnswer,
      raw.correctAnswerText,
      raw.CorrectAnswerText,
      raw.rightAnswer,
      raw.RightAnswer
    ) || '—',
    reason: readText(raw.reason, raw.Reason) || undefined,
    imagePath: readText(raw.imagePath, raw.ImagePath) || undefined,
  };
}

function unwrapLessons(body: ApiResponse<unknown>): WrongAnswerLesson[] | null {
  if (!body.success && body.success !== undefined) return null;
  const data = asRecord(body.data);
  const list = firstArray(
    body.data,
    data?.lessons,
    data?.Lessons,
    data?.items,
    data?.Items,
    data?.wrongAnswersByLesson,
    data?.WrongAnswersByLesson,
    data?.wrongAnswers,
    data?.WrongAnswers,
    body.message
  );
  if (!list) return body.success ? [] : null;
  return list
    .map((item) => (asRecord(item) ? normalizeLesson(asRecord(item)!) : null))
    .filter((item): item is WrongAnswerLesson => item !== null);
}

function unwrapLessonDetail(body: ApiResponse<unknown>, lessonId: number): LessonWrongAnswers | null {
  if (body.success === false) return null;
  const data = asRecord(body.data);
  const list = firstArray(
    body.data,
    data?.wrongQuestions,
    data?.WrongQuestions,
    data?.questions,
    data?.Questions,
    data?.wrongAnswers,
    data?.WrongAnswers,
    data?.items,
    data?.Items,
    body.message
  );
  if (!list) return body.success ? { lessonId, lessonName: '', courseName: '', questions: [] } : null;
  return {
    lessonId: readNumber(data?.lessonId, data?.LessonId) || lessonId,
    lessonName: readText(data?.lessonName, data?.LessonName, data?.quizName, data?.QuizName),
    courseName: readText(data?.courseName, data?.CourseName),
    questions: list
      .map((item) => (asRecord(item) ? normalizeQuestion(asRecord(item)!) : null))
      .filter((item): item is WrongAnswerItem => item !== null),
  };
}

function cleanSubmitPayload(payload: SubmitQuizWithAnswersRequest): SubmitQuizWithAnswersRequest {
  return {
    lessonId: payload.lessonId,
    notes: payload.notes,
    questions: payload.questions.map((question) => {
      const options = (question.options || [])
        .map((option) => ({
          text: String(option?.text ?? '').trim(),
          isCorrect: Boolean(option?.isCorrect),
        }))
        .filter((option) => option.text.length > 0);
      const studentAnswer = String(question.studentAnswer ?? '').trim();
      return {
        text: String(question.text ?? '').trim() || 'سؤال',
        type: question.type || 'MultipleChoice',
        imagePath: question.imagePath,
        reason: question.reason,
        options,
        studentAnswer: studentAnswer || '—',
        isCorrect: Boolean(question.isCorrect),
      };
    }),
  };
}

export const wrongAnswerService = {
  async submitWithAnswers(payload: SubmitQuizWithAnswersRequest) {
    try {
      const response = await api.post<ApiResponse<unknown>>(
        '/api/QuizResult/submit-with-answers',
        cleanSubmitPayload(payload)
      );
      const body = response.data;
      if (body && body.success === false) {
        throw new Error(getErrorMessage(body, 'فشل في إرسال نتيجة الاختبار'));
      }
      return body;
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: ApiResponse<unknown> }; message?: string };
      if (err.response?.status === 401) {
        throw new Error('انتهت الجلسة. سجّل الدخول مرة أخرى.');
      }
      if (err.response?.data) {
        throw new Error(getErrorMessage(err.response.data, 'فشل في إرسال نتيجة الاختبار'));
      }
      throw new Error(err.message || 'فشل في إرسال نتيجة الاختبار');
    }
  },

  async getMyWrongAnswers() {
    try {
      const response = await api.get<ApiResponse<unknown>>('/api/StudentWrongAnswer/my-wrong-answers');
      const lessons = unwrapLessons(response.data);
      if (!lessons) {
        return { success: false as const, error: getErrorMessage(response.data, 'تعذر تحميل أخطائك') };
      }
      return { success: true as const, data: lessons };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiResponse<unknown> } };
      return {
        success: false as const,
        error: getErrorMessage(err.response?.data, 'تعذر تحميل أخطائك'),
      };
    }
  },

  async getLessonWrongAnswers(lessonId: number) {
    try {
      const response = await api.get<ApiResponse<unknown>>(
        `/api/StudentWrongAnswer/my-wrong-answers/lesson/${lessonId}`
      );
      const detail = unwrapLessonDetail(response.data, lessonId);
      if (!detail) {
        return { success: false as const, error: getErrorMessage(response.data, 'تعذر تحميل أسئلة هذا الدرس') };
      }
      return { success: true as const, data: detail };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiResponse<unknown> } };
      return {
        success: false as const,
        error: getErrorMessage(err.response?.data, 'تعذر تحميل أسئلة هذا الدرس'),
      };
    }
  },
};
